'use client';

import { cn } from '~/lib/utils';
import { Price } from './price';
import React from 'react';
import { calcOriginalSalePrice } from '~/utils/products/savings-percentage-helper';
import { type Website } from '~/fecom-api/schema';

type ProductDetailsPriceProps = {
  className?: string;
  priceIncVat: number;
  priceExcVat: number;
  wasPriceIncVat?: number | null;
  wasPriceExcVat?: number | null;
  isPriceBreakPrice?: boolean;
  savingsPercentage?: number | null;
  currency?: string;
  websiteId: Website;
};

function ProductDetailsPriceInner({
  priceExcVat,
  priceIncVat,
  wasPriceExcVat,
  wasPriceIncVat,
  className,
  isPriceBreakPrice = false,
  savingsPercentage,
  currency,
  websiteId,
}: ProductDetailsPriceProps) {
  const hasWasPrice = !!wasPriceExcVat;

  return (
    <div className={cn(className)}>
      <p className="text-xl">
        <span
          className={cn(
            'text-xl font-semibold sm:text-2xl',
            hasWasPrice && 'text-success',
            !hasWasPrice && !!savingsPercentage && 'text-success',
          )}
        >
          <Price
            websiteId={websiteId}
            priceIncVat={priceIncVat}
            priceExcVat={priceExcVat}
            vatStatusClassName="text-black text-xs sm:text-sm"
            showVatStatusText
            savingsText={(isIncVat) => {
              // if saving is less than 5% do not show the % off text
              if (!!savingsPercentage && savingsPercentage >= 5) {
                return `(${savingsPercentage}% off)`;
              }

              if (!hasWasPrice || !wasPriceExcVat || !wasPriceIncVat) {
                return null;
              }

              const percentageSaving = isIncVat
                ? Math.floor(
                    ((wasPriceIncVat - priceIncVat) / wasPriceIncVat) * 100,
                  )
                : Math.floor(
                    ((wasPriceExcVat - priceExcVat) / wasPriceExcVat) * 100,
                  );

              // if saving is less than 5% do not show the % off text
              if (percentageSaving < 5) {
                return null;
              }

              return `(${percentageSaving}% off)`;
            }}
            isPriceBreakPrice={isPriceBreakPrice}
            currency={currency}
          />
        </span>
        {!hasWasPrice && !!savingsPercentage && (
          <span className="ml-4 text-base text-black/70">
            {' was '}
            <Price
              websiteId={websiteId}
              priceIncVat={calcOriginalSalePrice(
                priceIncVat,
                savingsPercentage,
              )}
              priceExcVat={calcOriginalSalePrice(
                priceExcVat,
                savingsPercentage,
              )}
              vatStatusClassName="text-current"
              currency={currency}
            />
          </span>
        )}
        {hasWasPrice && (
          <span className="ml-4 text-base text-black/70">
            {' was '}
            <Price
              websiteId={websiteId}
              priceIncVat={wasPriceIncVat ?? 0}
              priceExcVat={wasPriceExcVat ?? 0}
              vatStatusClassName="text-current"
            />
          </span>
        )}
      </p>
      <p className="">
        <Price
          websiteId={websiteId}
          priceIncVat={priceIncVat}
          priceExcVat={priceExcVat}
          vatStatusClassName="text-current text-xs sm:text-sm"
          showVatStatusText
          showOppositeVatStatus
          currency={currency}
        />
      </p>
    </div>
  );
}

export const ProductDetailsPrice = React.memo(ProductDetailsPriceInner);
